import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { CustomerVehicle, updateCustomerVehicle } from '../../lib/customers';

interface EditVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (vehicle: CustomerVehicle) => void;
  vehicle: CustomerVehicle | null;
}

export default function EditVehicleModal({
  isOpen,
  onClose,
  onUpdate,
  vehicle
}: EditVehicleModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<{
    make: string;
    model: string;
    year: number;
    vin: string;
    color: string;
    license_plate: string;
    mileage: number;
    notes: string;
    is_primary: boolean;
  }>({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vin: '', 
    color: '', 
    license_plate: '', 
    mileage: 0,
    notes: '',
    is_primary: false
  });

  useEffect(() => {
    if (isOpen && vehicle) {
      setIsAnimating(true);
      setFormData({
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        vin: vehicle.vin || '',
        color: vehicle.color || '',
        license_plate: vehicle.license_plate || '',
        mileage: vehicle.mileage || 0,
        notes: vehicle.notes || '',
        is_primary: vehicle.is_primary || false
      });
    }
  }, [isOpen, vehicle]);

  // Validate form whenever make or model changes
  useEffect(() => {
    setIsFormValid(formData.make.trim() !== '' && formData.model.trim() !== '');
  }, [formData.make, formData.model]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicle) return;

    try {
      setLoading(true);
      setError(null);

      const updatedVehicle = await updateCustomerVehicle(vehicle.id, formData);
      onUpdate(updatedVehicle);
      onClose();
    } catch (err) {
      console.error('Error updating vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to update vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!vehicle) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100000]`}
      onClick={onClose}
      style={{
        // Ensure modal appears above BookingInfoModal on mobile
        zIndex: 100000,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Edit Vehicle</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Make
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter make"
                    value={formData.make || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter model"
                    value={formData.model || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year
                  </label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={formData.year || new Date().getFullYear()}
                    onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Additional Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color
                    </label>
                    <input
                      type="text"
                      placeholder="Enter color"
                      value={formData.color || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      VIN
                    </label>
                    <input
                      type="text"
                      placeholder="Enter VIN"
                      value={formData.vin || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, vin: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      License Plate
                    </label>
                    <input
                      type="text"
                      placeholder="Enter license plate"
                      value={formData.license_plate || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  placeholder="Add any notes about this vehicle..."
                  value={formData.notes || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                />
              </div>

              {/* Preferences */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Preferences</h3>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_primary || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <span className="text-sm text-gray-700">Set as primary vehicle for this customer</span>
                </label>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}